<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { CaregiverFilter } from '@/api/caregivers';
import type { PatientFilter } from '@/api/patients';
import { dateToIso } from '@/utils/calendar-events';
import { usePermissions } from '@/composables/usePermissions';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import ShiftBatchDestroyForm from './shift-batch-destroy-form.vue';
import ShiftRecurrentCreateForm from './shift-recurrent-create-form.vue';
import NewShiftOfferingDialog from './new-shift-offering-dialog.vue';
import DeletedShiftsDialog from './deleted-shifts-dialog.vue';
import ShiftRecalculationDialog from './shift-recalculation-dialog.vue';

interface Props {
  title: string;
  dateTitle?: string;
  // First day of the displayed period; preselects the date picker.
  anchorDate?: string;
  loading: boolean;
  pdfDownloading: boolean;
  // Care-planning companies on a patient-oriented view also get the PDF that prints
  // the weekly-plan visits no shift covers.
  showRequirementsPdf?: boolean;
  excelDownloading: boolean;
  currentPatient?: PatientFilter;
  currentCaregiver?: CaregiverFilter;
  deletedShiftsDateRange: { startDate: string, endDate: string };
}

const props = defineProps<Props>();

const emit = defineEmits<{
  today: [],
  previous: [],
  next: [],
  dateSelected: [date: string],
  downloadPdf: [],
  downloadPdfWithRequirements: [],
  downloadExcel: [],
  success: [],
  refetch: [],
}>();

const { t } = useI18n();
const { hasPermission } = usePermissions();

const createMenuActive = ref(false);

// ShiftPolicy#recalculate?: a permission of its own, granted by hand, plus the amounts.
const canRecalculate = computed(() => hasPermission.value('shifts', 'recalculate') &&
  hasPermission.value('shifts', 'read_financial_information'));

const datePickerOpen = ref(false);

const anchorDateValue = computed(() => {
  if (!props.anchorDate) return undefined;
  const [year, month, day] = props.anchorDate.split('-').map(Number);

  return new Date(year, (month ?? 1) - 1, day);
});

function onDatePicked(value: unknown) {
  if (!(value instanceof Date)) return;

  datePickerOpen.value = false;
  emit('dateSelected', dateToIso(value));
}

const shiftsNewUrl = computed(() => {
  if (props.currentPatient) {
    return `/shifts/new?patient_id=${props.currentPatient.id}`;
  }

  return '/shifts/new';
});
</script>
<template>
  <v-toolbar
    color="white"
    flat
  >
    <v-toolbar-title class="font-weight-bold">
      {{ title }}
      <v-btn
        v-if="currentPatient"
        icon="mdi-account-eye"
        color="black"
        size="small"
        variant="text"
        class="pb-1 ma-4"
        :href="`/patients/${currentPatient?.id}`"
      >
        <v-icon icon="mdi-account-eye" />
        <v-tooltip
          activator="parent"
          location="top"
        >
          {{ t('shiftsCalendar.toolbar.viewProfile') }}
        </v-tooltip>
      </v-btn>
      <v-btn
        icon="mdi-file-pdf-box"
        color="black"
        size="small"
        variant="text"
        class="pb-1 ma-2"
        :disabled="loading || pdfDownloading"
        @click="emit('downloadPdf')"
      >
        <v-icon icon="mdi-file-pdf-box" />
        <v-tooltip
          activator="parent"
          location="top"
        >
          {{ t('shiftsCalendar.toolbar.downloadPdf') }}
        </v-tooltip>
      </v-btn>
      <v-btn
        v-if="showRequirementsPdf"
        icon="mdi-file-pdf-box"
        color="primary"
        size="small"
        variant="text"
        class="pb-1 ma-2 download-pdf-with-requirements"
        :disabled="loading || pdfDownloading"
        @click="emit('downloadPdfWithRequirements')"
      >
        <v-icon icon="mdi-file-pdf-box" />
        <v-tooltip
          activator="parent"
          location="top"
        >
          {{ t('shiftsCalendar.toolbar.downloadPdfWithRequirements') }}
        </v-tooltip>
      </v-btn>
      <v-btn
        icon="mdi-file-excel-box"
        color="black"
        size="small"
        variant="text"
        class="pb-1 ma-2"
        :disabled="loading || excelDownloading"
        @click="emit('downloadExcel')"
      >
        <v-icon icon="mdi-file-excel-box" />
        <v-tooltip
          activator="parent"
          location="top"
        >
          {{ t('shiftsCalendar.toolbar.downloadExcel') }}
        </v-tooltip>
      </v-btn>
      <deleted-shifts-dialog
        v-if="(currentPatient || currentCaregiver) &&
          hasPermission('shifts', 'index') &&
          deletedShiftsDateRange.startDate"
        :patient-id="currentPatient?.id"
        :caregiver-id="currentCaregiver?.id"
        :filter-name="currentPatient?.fullName || currentCaregiver?.fullName || ''"
        :filter-type="currentPatient ? t('shiftsCalendar.patient') : t('shiftsCalendar.caregiver')"
        :start-date="deletedShiftsDateRange.startDate"
        :end-date="deletedShiftsDateRange.endDate"
      />
    </v-toolbar-title>
  </v-toolbar>
  <v-toolbar
    color="white"
    flat
    class="date-nav-toolbar"
  >
    <div class="date-nav-toolbar__nav d-flex align-center">
      <v-btn
        variant="outlined"
        size="small"
        class="ml-3"
        :disabled="loading"
        @click="emit('today')"
      >
        {{ t('shiftsCalendar.toolbar.today') }}
      </v-btn>
      <v-btn
        icon="mdi-chevron-left"
        :disabled="loading"
        @click="emit('previous')"
      />
      <v-btn
        icon="mdi-chevron-right"
        :disabled="loading"
        @click="emit('next')"
      />
      <p class="date-nav-toolbar__title text-truncate mb-0 ml-2">
        {{ dateTitle }}
      </p>
      <v-menu
        v-model="datePickerOpen"
        :close-on-content-click="false"
      >
        <template #activator="{ props: activatorProps }">
          <v-btn
            v-bind="activatorProps"
            icon="mdi-calendar"
            variant="text"
            size="small"
            :disabled="loading"
            :aria-label="t('shiftsCalendar.toolbar.pickDate')"
          />
        </template>
        <v-date-picker
          :model-value="anchorDateValue"
          @update:model-value="onDatePicked"
        />
      </v-menu>
    </div>
    <v-spacer class="date-nav-toolbar__spacer" />
    <div class="date-nav-toolbar__actions justify-end mr-4 d-flex align-center">
      <v-btn
        v-if="hasPermission('shifts', 'create')"
        class="mb-2 text-white border bg-primary"
      >
        {{ t('shiftsCalendar.toolbar.add') }}
        <v-menu
          v-model="createMenuActive"
          activator="parent"
          transition="scale-transition"
          location="top"
        >
          <v-list>
            <responsive-dialog
              max-width="800"
              scrollable
            >
              <template #activator="{ props: activatorProps }">
                <v-list-item v-bind="activatorProps">
                  <v-list-item-title>{{ t('shiftsCalendar.toolbar.recurrent') }}</v-list-item-title>
                </v-list-item>
              </template>
              <template #default="{ close }">
                <shift-recurrent-create-form
                  :patient="currentPatient"
                  :caregiver="currentCaregiver"
                  @close="close(); createMenuActive = false"
                  @success="emit('success')"
                  @refetch="emit('refetch')"
                />
              </template>
            </responsive-dialog>
            <v-list-item :href="shiftsNewUrl">
              <v-list-item-title>{{ t('shiftsCalendar.toolbar.manual') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-btn>
      <responsive-dialog
        v-if="currentPatient"
        max-width="500"
        scrollable
      >
        <template #activator="{ props: activatorProps }">
          <v-btn
            v-if="hasPermission('shifts', 'destroy')"
            v-bind="activatorProps"
            class="mb-2 ml-4 text-red"
            variant="outlined"
          >
            {{ t('shiftsCalendar.toolbar.stop') }}
          </v-btn>
        </template>
        <template #default="{ close }">
          <shift-batch-destroy-form
            :patient="currentPatient"
            @close="close()"
            @success="emit('success')"
            @refetch="emit('refetch')"
          />
        </template>
      </responsive-dialog>
      <responsive-dialog
        v-if="currentPatient && canRecalculate"
        max-width="1200"
        scrollable
      >
        <template #activator="{ props: activatorProps }">
          <v-btn
            v-bind="activatorProps"
            class="mb-2 ml-4"
            color="primary"
            variant="outlined"
            size="small"
            icon
            :aria-label="t('shiftsCalendar.toolbar.recalculate')"
          >
            <v-icon icon="mdi-calculator-variant" />
            <v-tooltip
              activator="parent"
              location="bottom"
            >
              {{ t('shiftsCalendar.toolbar.recalculate') }}
            </v-tooltip>
          </v-btn>
        </template>
        <template #default="{ close }">
          <shift-recalculation-dialog
            :patient="currentPatient"
            :start-date="deletedShiftsDateRange.startDate"
            :end-date="deletedShiftsDateRange.endDate"
            @close="close()"
            @success="emit('refetch')"
          />
        </template>
      </responsive-dialog>
      <responsive-dialog
        v-if="currentPatient"
        max-width="800"
        scrollable
      >
        <template #activator="{ props: activatorProps }">
          <v-btn
            v-if="hasPermission('shifts', 'create')"
            v-bind="activatorProps"
            class="mb-2 ml-4"
            color="primary"
            variant="outlined"
            size="small"
            icon
          >
            <v-icon icon="mdi-email-arrow-right" />
            <v-tooltip
              activator="parent"
              location="bottom"
            >
              {{ t('shiftsCalendar.toolbar.offerShift') }}
            </v-tooltip>
          </v-btn>
        </template>
        <template #default="{ close }">
          <new-shift-offering-dialog
            :patient="currentPatient"
            @close="close()"
            @success="emit('success'); close()"
          />
        </template>
      </responsive-dialog>
    </div>
  </v-toolbar>
</template>
<style scoped>
/* Hoy / prev / next / date title stay together on one row; the title takes the
   remaining width and ellipsizes instead of wrapping. */

.date-nav-toolbar__nav {
  flex: 1 1 auto;
  min-width: 0;
}

.date-nav-toolbar__title {
  min-width: 0;
}

/* The date-navigation toolbar (Hoy / prev / next / title + Agregar / batch
   actions) overflows a 360px phone; below the phone breakpoint the nav row and
   the actions row each take a full row — desktop keeps its single-row layout. */
@media (max-width: 600px) {
  .date-nav-toolbar.v-toolbar {
    height: auto !important;
  }

  .date-nav-toolbar :deep(.v-toolbar__content) {
    /* !important is required: Vuetify pins the content box to --v-toolbar-height,
       so a plain `height: auto` loses and the wrapped rows (nav + Agregar /
       batch actions) stay clipped to 64px and overlap the calendar below. */
    height: auto !important;
    align-items: flex-start;
    flex-wrap: wrap;
    row-gap: 4px;
    padding-block: 4px;
  }

  .date-nav-toolbar__nav {
    flex: 1 1 100%;
  }

  /* Collapse the spacer so the actions wrap directly under the nav row. */
  .date-nav-toolbar__spacer {
    display: none;
  }

  .date-nav-toolbar__actions {
    flex: 1 1 100%;
    justify-content: flex-start !important;
    margin-right: 0 !important;
  }
}
</style>
